'use client'

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { TierMedallion } from './medallions/tier-medallion'
import { RayBurstBackdrop } from './medallions/ray-burst-backdrop'
import { StarIcon } from './medallions/star-icon'
import type { TierKey } from '@/lib/phishtrainer/types'
import { TIER_DEFS } from '@/lib/phishtrainer/types'
import { Sparkles, X } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { richText } from '@/components/user-portal/learner-i18n'
import { tierName } from './catalogue-text'

/**
 * Tier-up celebration overlay. Renders when a tier upgrade is detected.
 * Controller reads `lastKnownTier` from localStorage, compares to current,
 * and conditionally renders this with the new tier + old tier.
 */
export function TierUpCelebration({
  newTier,
  onDismiss,
}: {
  newTier: TierKey
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
    // Auto-focus: allow ESC to dismiss
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDismiss])

  const tierDef = TIER_DEFS.find((tier) => tier.key === newTier)
  if (!tierDef) return null
  const name = tierName(t, tierDef.key, tierDef.name)

  return (
    <div
      role="dialog"
      aria-label={t('learner.cards.tierUp.dialogLabel', { tier: name })}
      className={
        'fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-md transition-opacity duration-300 ' +
        (mounted ? 'opacity-100' : 'opacity-0')
      }
    >
      {/* Confetti dots */}
      <Confetti />

      {/* Close */}
      <button
        aria-label={t('learner.cards.common.dismiss')}
        onClick={onDismiss}
        className="absolute top-4 end-4 h-10 w-10 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative max-w-lg w-full text-center px-6">
        <div className="relative overflow-hidden rounded-3xl border-2 border-primary/40 bg-card p-8 sm:p-12">
          <RayBurstBackdrop />
          <div className="relative space-y-5">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/30 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-primary font-semibold">
              <Sparkles className="h-3 w-3" /> {t('learner.cards.tierUp.levelUp')}
            </div>
            <div
              className={'mx-auto transition-transform duration-500 ' + (mounted ? 'scale-100' : 'scale-0')}
            >
              <TierMedallion tier={newTier} className="w-40 sm:w-52 mx-auto" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t('learner.cards.tierUp.youReached')}</div>
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent mt-1">
                {name}
              </h2>
              <p className="text-sm text-muted-foreground mt-3">
                {tierDef.threshold > 0
                  ? richText(t('learner.cards.tierUp.crossed', { count: tierDef.threshold }), {
                      stars: (
                        <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                          <StarIcon size={14} /> {tierDef.threshold}
                        </span>
                      ),
                      tier: <span className="font-semibold text-foreground">{name}</span>,
                    })
                  : t('learner.cards.tierUp.welcome')}
              </p>
            </div>
            <Button size="lg" onClick={onDismiss} className="mt-4">
              {t('learner.cards.tierUp.keepClimbing')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Confetti() {
  // 30 little colored circles with random positions + fall animations via inline style.
  const pieces = Array.from({ length: 30 }).map((_, i) => {
    const left = (i * 37) % 100
    const delay = (i % 7) * 0.12
    const duration = 2 + (i % 4) * 0.5
    const color = ['#ec4899', '#a855f7', '#f59e0b', '#10b981', '#3b82f6'][i % 5]
    const size = 8 + (i % 3) * 4
    return (
      <span
        key={i}
        className="absolute top-0 rounded-full animate-confetti-fall"
        style={{
          left: `${left}%`,
          width: size,
          height: size,
          backgroundColor: color,
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`,
        }}
      />
    )
  })
  return <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">{pieces}</div>
}
